import React from 'react';

import styled from 'styled-components';

import ConfirmModal from 'common/Modal/ConfirmModal';
import ErrorMessage from 'components/lib/ErrorMessage';

import ActionPlanDeleteWithReasonModal from './ActionPlanDeleteWithReasonModal';
import ActionPlansFilters from './ActionPlansFilters';
import ActionPlansHeader from './ActionPlansHeader';
import ActionPlansTable from './ActionPlansTable';
import ActionPlansTabs from './ActionPlansTabs';
import CreateEditActionPlanModal from './CreateEditActionPlanModal';
import useActionPlanDeleteFlow from './useActionPlanDeleteFlow';
import useActionPlanFocusAreas from './useActionPlanFocusAreas';
import useActionPlansPage from './useActionPlansPage';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 10px 30px;
`;

const ActionPlans = () => {
  const {
    currentUser,
    selectedCommunity,
    currentUserDisplayName,
    showAlerts,
    activeTab,
    page,
    setPage,
    search,
    sourceCategories,
    alertTypeGroups,
    focusAreaFilters,
    ownerFilters,
    statusFilters,
    changeTab,
    handleSearchChange,
    handleSourceCategoriesChange,
    handleAlertTypesChange,
    handleFocusAreaFiltersChange,
    handleOwnerFiltersChange,
    handleStatusFiltersChange,
    resetFilters,
    data,
    isLoading,
    isError,
    metrics,
    visiblePlans,
    ownerFilterOptions,
    assignPlanToMe,
    updatePlanStatus,
    removePlan,
    isRemovingPlan,
    createOpen,
    isEditing,
    editPlan,
    form,
    ownerOptions,
    itemTypeOptions,
    isSavingPlan,
    openCreateModal,
    openEditModal,
    closeModal,
    submitPlan,
  } = useActionPlansPage();

  const { focusAreaOptions, focusAreaLabelById } = useActionPlanFocusAreas();

  const { confirmModalProps, deleteWithReasonModalProps, openDeletePlanConfirm } = useActionPlanDeleteFlow({
    removePlan,
    isRemovingPlan,
  });

  return (
    <Container data-test-id="action-plans-page">
      <ActionPlansHeader onCreatePlan={openCreateModal} />
      <Main data-test-id="action-plans-main">
        <div data-test-id="action-plans-error-message">
          <ErrorMessage text="Failed to load action plans. Please try again." active={isError} />
        </div>
        <ActionPlansTabs metrics={metrics} activeTab={activeTab} onChange={changeTab} />
        <ActionPlansFilters
          activeTab={activeTab}
          search={search}
          sourceCategories={sourceCategories}
          alertTypes={alertTypeGroups}
          focusAreas={focusAreaFilters}
          owners={ownerFilters}
          statuses={statusFilters}
          focusAreaOptions={focusAreaOptions}
          ownerOptions={ownerFilterOptions}
          onSearchChange={handleSearchChange}
          onSourceCategoriesChange={handleSourceCategoriesChange}
          onAlertTypesChange={handleAlertTypesChange}
          onFocusAreasChange={handleFocusAreaFiltersChange}
          onOwnersChange={handleOwnerFiltersChange}
          onStatusesChange={handleStatusFiltersChange}
          onReset={resetFilters}
        />
        <ActionPlansTable
          plans={visiblePlans}
          loading={isLoading}
          page={page}
          totalCount={data?.count ?? 0}
          onPageChange={setPage}
          currentUserDisplayName={currentUserDisplayName}
          focusAreaLabelById={focusAreaLabelById}
          hasCurrentUser={Boolean(currentUser)}
          selectedCommunityId={selectedCommunity?.id}
          showRelatedAlert={Boolean(showAlerts)}
          onAssignToMe={assignPlanToMe}
          onUpdateStatus={updatePlanStatus}
          onEditPlan={openEditModal}
          onDeletePlan={(plan) =>
            openDeletePlanConfirm({
              id: plan.id,
              name: plan.name,
              source: plan.source,
            })
          }
        />
      </Main>

      <CreateEditActionPlanModal
        categoryOptions={focusAreaOptions}
        itemTypeOptions={itemTypeOptions}
        open={createOpen}
        isEditing={isEditing}
        isSaving={isSavingPlan}
        form={form}
        ownerOptions={ownerOptions}
        onCancel={closeModal}
        onDelete={() => {
          if (!editPlan) {
            return;
          }
          openDeletePlanConfirm({
            id: editPlan.id,
            name: editPlan.name,
            source: editPlan.source,
            onSuccess: closeModal,
          });
        }}
        onSubmit={submitPlan}
      />

      <ConfirmModal {...confirmModalProps} />
      <ActionPlanDeleteWithReasonModal {...deleteWithReasonModalProps} />
    </Container>
  );
};

export default ActionPlans;
